import React, { useMemo, useCallback } from 'react';
import { Marker, OverlayView, OverlayViewF, Polygon } from '@react-google-maps/api';
import useMapsStore from '../../store/useMapsStore';
import { checkStatusAvailability, getPolygonColors, getPolygonIndex, getShapeCenter, mapStatus } from '../../util/polygon';
import { BRAND_CCS } from 'common/types/community';
import { MAP_DEFAULT_ZOOM, POLYGON_LOT_MAP_STATUS_UNAVAILABLE, POLYGON_TYPE_COMMUNITY, POLYGON_TYPE_LOT, POLYGON_VISIBLE_ZOOM_BREAKPOINT, SHAPE_TYPE_PIN, SHAPE_TYPE_POLYGON } from '../../util/constants';
import PopupHoverContent from '../map-popup/PopupHoverContent';
import PopupClickContent from '../map-popup/PopupClickContent';
import PopupShape from '../map-popup/PopupShape';
import DetailLotCard from '../detail-lot-card/DetailLotCard';
import PopupPINorPOIContent from '../map-popup/PopupPINorPOIContent';
import usePolygonEventStore from '_viewport-maps/src/store/usePolygonEventStore';
import { useGoogleMap } from '@react-google-maps/api';
import useFiltersStore from '_viewport-maps/src/store/useFiltersStore';
function MapShape({ isMobile = false, polygon, index, isHovered, isClicked }) {
    const { brand, zoom, setZoom } = useMapsStore();
    const { lotStatuses } = useFiltersStore();
    const { setHoveredPolygonIndex, setClickedPolygonIndex, setClickedLotId, setHoveredLotId, clickedPolygonIndex } = usePolygonEventStore([
        "setHoveredPolygonIndex",
        "setClickedPolygonIndex",
        "setClickedLotId",
        "setHoveredLotId",
        "clickedPolygonIndex"
    ]);
    const polygonBrand = polygon.Properties.Brand;
    const shapePolygonType = polygon.Type;
    const polygonType = polygon.Properties.Type;
    const map = useGoogleMap();
    let polygonColors = getPolygonColors(polygon, brand, isClicked, isHovered);
    let memoizedOverlayPosition;
    if (polygonType == POLYGON_TYPE_LOT) {
        memoizedOverlayPosition = useMemo(() => getShapeCenter(polygon.Coordinates), [JSON.stringify(polygon.Coordinates[0])]);
    }
    /**
     * @param polygon
     * Add mouse up listener to handle vertex or edge polygon change and set active polygon
     * @param mouseUpEvent
     * @param index
     */
    const handlePolygonMouseUp = (mouseUpEvent, index, polygon) => {
        if (polygon?.Properties.Type != POLYGON_TYPE_COMMUNITY &&
            checkStatusAvailability(mapStatus(polygon))) {
            setClickedLotId(polygon?.Properties.Id);
            setClickedPolygonIndex(index);
            setZoom(MAP_DEFAULT_ZOOM);
            if (polygon && (isMobile || zoom !== MAP_DEFAULT_ZOOM)) {
                const polygonCenter = getShapeCenter(polygon.Coordinates);
                polygonCenter.lat = polygonCenter.lat + 0.0022; //move map a little bit under center of polygon
                map?.panTo(polygonCenter);
            }
        }
    };
    const handlePolygonMouseDown = useCallback((mouseUpEvent, index) => {
        if (polygon?.Properties.Type != POLYGON_TYPE_COMMUNITY) {
            setClickedLotId(undefined);
            setClickedPolygonIndex(-1);
        }
    }, [isClicked, isHovered]);
    const handlePolygonMouseOver = useCallback((mouseUpEvent, index) => {
        if (polygon?.Properties.Type != POLYGON_TYPE_COMMUNITY) {
            setHoveredLotId(polygon.Properties.Id);
            setHoveredPolygonIndex(index);
        }
    }, [isClicked, isHovered]);
    const handlePolygonMouseOut = useCallback((mouseUpEvent, index) => {
        if (polygon?.Properties.Type != POLYGON_TYPE_COMMUNITY) {
            setHoveredLotId(undefined);
            setHoveredPolygonIndex(-1);
        }
    }, [isClicked, isHovered]);
    const getFontSize = (zoom) => {
        if (zoom > 18) {
            return zoom - 6;
        }
        else if (zoom > 17) {
            return zoom - 7;
        }
        else if (zoom > 16) {
            return zoom - 8;
        }
        else if (zoom > 15) {
            return zoom - 10;
        }
        else if (zoom > 14) {
            return zoom - 11;
        }
        else if (zoom > 13) {
            return (zoom - 12);
        }
        else
            return 0;
    };
    // if polygon brand is not set or is current brand, show polygon
    return polygonBrand === undefined || polygonBrand === '' || polygonBrand === brand ? (React.createElement(React.Fragment, null,
        ((shapePolygonType == SHAPE_TYPE_POLYGON && zoom >= POLYGON_VISIBLE_ZOOM_BREAKPOINT) ||
            polygonType != POLYGON_TYPE_COMMUNITY) && (React.createElement(Polygon, { path: polygon.Coordinates[0].map(([lat, lng]) => ({ lat, lng })), options: {
                ...polygonColors,
                zIndex: getPolygonIndex(polygon)
            }, onMouseDown: (mouseUpEvent) => handlePolygonMouseDown(mouseUpEvent, index), onMouseUp: (mouseUpEvent) => handlePolygonMouseUp(mouseUpEvent, index, polygon), onMouseOver: (mouseUpEvent) => handlePolygonMouseOver(mouseUpEvent, index), onMouseOut: (mouseUpEvent) => handlePolygonMouseOut(mouseUpEvent, index), editable: false, draggable: false })),
        polygonType == POLYGON_TYPE_COMMUNITY && (React.createElement(Polygon, { path: polygon.Coordinates[0].map(([lat, lng]) => ({ lat, lng })), options: {
                ...polygonColors,
                zIndex: getPolygonIndex(polygon)
            }, editable: false, draggable: false })),
        polygonType == POLYGON_TYPE_LOT &&
            polygon?.Properties?.LotId !== undefined && polygon?.Properties?.LotId !== null &&
            zoom >= POLYGON_VISIBLE_ZOOM_BREAKPOINT && (React.createElement(OverlayViewF, { position: memoizedOverlayPosition, mapPaneName: OverlayView.OVERLAY_MOUSE_TARGET, getPixelPositionOffset: (width, height) => ({
                x: -(width / 2),
                y: -(height / 2)
            }) },
            React.createElement("div", { className: (polygon.Properties.LotIdOrientation === 'Vertical' ? 'map-shape__lot-number--vertical' : 'map-shape__lot-number') +
                    (isHovered || polygon.hideOnMap || mapStatus(polygon) === POLYGON_LOT_MAP_STATUS_UNAVAILABLE ? ' dark-text' : ''), style: { fontSize: `${getFontSize(zoom)}px`, fontWeight: `600` } }, polygon?.Properties?.LotId?.replace(/^0+/, '')))),
        shapePolygonType == SHAPE_TYPE_PIN && zoom >= POLYGON_VISIBLE_ZOOM_BREAKPOINT && (React.createElement(Marker, { position: {
                lat: polygon.Coordinates[0][0][0],
                lng: polygon.Coordinates[0][0][1]
            }, icon: brand === BRAND_CCS
                ? '/Assets/images/century-map/pin-viewport-css-icon.svg'
                : '/Assets/images/century-map/pin-viewport-cmp-icon.svg', onMouseDown: (mouseUpEvent) => handlePolygonMouseDown(mouseUpEvent, index), onMouseUp: (mouseUpEvent) => handlePolygonMouseUp(mouseUpEvent, index), onMouseOver: (mouseUpEvent) => handlePolygonMouseOver(mouseUpEvent, index), onMouseOut: (mouseUpEvent) => handlePolygonMouseOut(mouseUpEvent, index), draggable: false })),
        isHovered &&
            clickedPolygonIndex < 0 && // hide hover popup when we have clicked popup
            !isMobile &&
            zoom >= POLYGON_VISIBLE_ZOOM_BREAKPOINT && (React.createElement(PopupShape, { shapeType: shapePolygonType, polygon: polygon, index: index, zIndex: 100, onClose: () => {
                setHoveredLotId(undefined);
                setHoveredPolygonIndex(-1);
            } },
            React.createElement(PopupHoverContent, { polygon: polygon }))),
        isClicked &&
            zoom >= POLYGON_VISIBLE_ZOOM_BREAKPOINT &&
            checkStatusAvailability(mapStatus(polygon)) && (React.createElement(PopupShape, { shapeType: shapePolygonType, polygon: polygon, index: index, zIndex: 50, options: {
                disableAutoPan: isMobile //enable custom moving to position for popup only for mobile
            }, onClose: () => {
                setClickedLotId(undefined);
                setClickedPolygonIndex(-1);
            } },
            React.createElement(PopupClickContent, { polygonType: polygon.Properties.Type, lotContent: React.createElement("ul", null,
                    React.createElement(DetailLotCard, { polygon: polygon, onClose: () => {
                            setClickedLotId(undefined);
                            setClickedPolygonIndex(-1);
                        } })), pinContent: React.createElement(PopupPINorPOIContent, { description: polygon.Properties.Name }) }))))) : (React.createElement(React.Fragment, null));
}
export default React.memo(MapShape);
